/**
 * BlockSuite React Editor Components
 * Uses the properly registered custom elements from BlockSuite
 */

import React, { useEffect, useRef, useMemo } from 'react'
import { DocCollection, Schema, Text } from '@blocksuite/store'
import { AffineSchemas } from '@blocksuite/blocks'
import { PageEditorBlockSpecs, EdgelessEditorBlockSpecs } from '@blocksuite/blocks'
import { BlockStdScope } from '@blocksuite/block-std'
import { createReactComponentFromLit } from './react-lit-bridge'

// Import effects to ensure custom elements are registered
import './effects'

export type DocMode = 'page' | 'edgeless'

interface BlockSuiteEditorProps {
  documentId: string
  mode?: DocMode
  onContentChange?: (content: any) => void
  initialContent?: string
}

// Create React components for the registered custom elements
const PageRootElement = createReactComponentFromLit<HTMLElement>({
  tagName: 'affine-page-root',
  displayName: 'PageRootElement'
})

const EdgelessRootElement = createReactComponentFromLit<HTMLElement>({
  tagName: 'affine-edgeless-root', 
  displayName: 'EdgelessRootElement'
})

export function BlockSuiteEditor({ 
  documentId,
  mode = 'page',
  onContentChange,
  initialContent = ''
}: BlockSuiteEditorProps) {
  const [isDocReady, setIsDocReady] = React.useState(false)
  
  const { collection, doc, std } = useMemo(() => {
    // Create collection with schema
    const schema = new Schema()
    schema.register(AffineSchemas)
    const collection = new DocCollection({ schema })
    collection.meta.initialize()

    // Create document
    const doc = collection.createDoc({ id: documentId })
    
    // Create BlockStdScope with appropriate specs
    const specs = mode === 'page' ? PageEditorBlockSpecs : EdgelessEditorBlockSpecs
    const std = new BlockStdScope({
      doc,
      extensions: specs
    })

    return { collection, doc, std }
  }, [documentId, mode])

  // Initialize document structure after components are ready
  useEffect(() => {
    setIsDocReady(false)
    
    // Initialize document structure synchronously
    try {
      // Load the document first
      doc.load(() => {
        // Check if document already has blocks
        const existingBlocks = Object.values(doc.blocks.value || {})
        if (existingBlocks.length > 0) {
          setIsDocReady(true)
          return
        }
        
        // Create the document structure
        const pageBlockId = doc.addBlock('affine:page', {
          title: new Text()
        })
        
        const noteId = doc.addBlock('affine:note', {}, pageBlockId)
        
        // Add initial content if provided
        if (initialContent) {
          doc.addBlock('affine:paragraph', {
            text: new Text(initialContent)
          }, noteId)
        } else {
          doc.addBlock('affine:paragraph', {
            text: new Text()
          }, noteId)
        }
        
        // Ensure all blocks are properly initialized
        const checkBlocksReady = () => {
          const blocks = Object.values(doc.blocks.value || {})
          const allBlocksHaveModels = blocks.every((block: any) => block && block.id)
          
          if (allBlocksHaveModels && blocks.length >= 3) { // page, note, paragraph
            setIsDocReady(true)
          } else {
            setTimeout(checkBlocksReady, 50)
          }
        }
        
        checkBlocksReady()
      })
    } catch (error) {
      console.error('Failed to initialize document:', error)
      // Set ready anyway to prevent infinite loading
      setTimeout(() => setIsDocReady(true), 1000)
    }

    return () => {
      setIsDocReady(false)
    }
  }, [doc, initialContent])

  // Handle content changes
  useEffect(() => {
    if (!onContentChange || !isDocReady) return

    const disposable = doc.slots.blockUpdated.on(() => {
      const content = {
        blocks: Array.from(Object.values(doc.blocks.value || {})).map((block: any) => ({
          id: block.id,
          flavour: block.flavour,
          text: block.text?.toString() || ''
        }))
      }
      onContentChange(content)
    })

    return () => disposable.dispose()
  }, [doc, onContentChange, isDocReady])

  // Don't render editor until document is ready
  if (!isDocReady) {
    return (
      <div 
        style={{ 
          width: '100%', 
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
          fontSize: '14px'
        }}
      >
        Loading BlockSuite editor...
      </div>
    )
  }

  // Render the appropriate editor based on mode
  const editorProps = {
    doc,
    std,
    mode,
    style: {
      width: '100%',
      height: '100%',
      display: 'block'
    }
  }

  if (mode === 'edgeless') {
    return <EdgelessRootElement {...editorProps} />
  }
  
  return <PageRootElement {...editorProps} />
}

// Export individual editor components
export const PageEditor = ({ documentId, onContentChange, initialContent }: Omit<BlockSuiteEditorProps, 'mode'>) => (
  <BlockSuiteEditor 
    documentId={documentId} 
    mode="page" 
    onContentChange={onContentChange}
    initialContent={initialContent}
  />
)

export const EdgelessEditor = ({ documentId, onContentChange, initialContent }: Omit<BlockSuiteEditorProps, 'mode'>) => (
  <BlockSuiteEditor 
    documentId={documentId} 
    mode="edgeless" 
    onContentChange={onContentChange}
    initialContent={initialContent}
  />
)